import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import type { ApiEnvelope } from '@/types/api'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const ENABLE_MOCK = import.meta.env.VITE_ENABLE_MOCK === 'true'
const API_TOKEN = (import.meta.env.VITE_API_TOKEN || '').trim()
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete'])

let csrfToken: string | null = null
let csrfTokenPromise: Promise<string> | null = null

type ClientErrorKind = 'unauthorized' | 'forbidden' | 'network' | 'cors' | 'server' | 'unknown'
type ClientApiError = Error & {
  code: string
  status?: number
  kind?: ClientErrorKind
  errors?: unknown
}

function buildApiError(
  message: string,
  {
    code,
    status,
    kind = 'unknown',
    errors,
  }: {
    code: string
    status?: number
    kind?: ClientErrorKind
    errors?: unknown
  },
): ClientApiError {
  const error = new Error(message) as ClientApiError
  error.code = code
  error.status = status
  error.kind = kind
  error.errors = errors
  return error
}

function normalizedUrl(url?: string) {
  return url || ''
}

function isUnsafeMethod(method?: string) {
  return UNSAFE_METHODS.has((method || 'get').toLowerCase())
}

function isCsrfExemptUrl(url?: string) {
  const value = normalizedUrl(url)
  return value.endsWith('/auth/login') || value.endsWith('/auth/csrf')
}

function isFormDataPayload(data: unknown): data is FormData {
  return typeof FormData !== 'undefined' && data instanceof FormData
}

function removeContentTypeHeader(headers: Record<string, unknown>) {
  delete headers['Content-Type']
  delete headers['content-type']
}

function clearCsrfToken() {
  csrfToken = null
  csrfTokenPromise = null
}

async function fetchCsrfToken() {
  if (csrfToken) return csrfToken
  if (!csrfTokenPromise) {
    const csrfUrl = `${BASE_URL.replace(/\/$/, '')}/auth/csrf`
    csrfTokenPromise = axios
      .get<ApiEnvelope<{ csrf_token: string }>>(csrfUrl, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        const body = response.data
        const token = body.data?.csrf_token
        if (!body.success || !token) {
          throw new Error(body.message || 'CSRF token unavailable')
        }
        csrfToken = token
        return token
      })
      .finally(() => {
        csrfTokenPromise = null
      })
  }
  return csrfTokenPromise
}

const client: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  // Must stay above the server-side upstream budget (LEGACY_AFTER_SALES_TIMEOUT_SECONDS,
  // default 20s) so a slow dependency surfaces as the server's own error message
  // instead of an opaque browser abort.
  timeout: 25000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截：按认证模式注入请求头
client.interceptors.request.use(async (config) => {
  const { actor, roles, authMode, apiToken } = useAuthStore.getState()

  if (authMode === 'token') {
    const token = apiToken || API_TOKEN
    if (token) {
      config.headers['X-API-Token'] = token
    }
    delete config.headers['X-Actor']
    delete config.headers['X-Roles']
    delete config.headers['X-CSRF-Token']
  } else if (authMode === 'dev_header') {
    if (actor) {
      config.headers['X-Actor'] = actor
    }
    if (roles.length > 0) {
      config.headers['X-Roles'] = roles.join(',')
    }
    delete config.headers['X-API-Token']
    delete config.headers['X-CSRF-Token']
  } else {
    delete config.headers['X-Actor']
    delete config.headers['X-Roles']
    delete config.headers['X-API-Token']
    if (isUnsafeMethod(config.method) && !isCsrfExemptUrl(config.url)) {
      config.headers['X-CSRF-Token'] = await fetchCsrfToken()
    } else {
      delete config.headers['X-CSRF-Token']
    }
  }

  // 若启用 mock，标记跳过真实请求
  if (ENABLE_MOCK) {
    config.headers['X-Mock-Mode'] = 'true'
  }

  if (isFormDataPayload(config.data)) {
    removeContentTypeHeader(config.headers as Record<string, unknown>)
  }

  return config
})

// 响应拦截：统一解析 envelope
client.interceptors.response.use(
  (response) => {
    const url = normalizedUrl(response.config.url)
    if (url.endsWith('/auth/login') || url.endsWith('/auth/logout')) {
      clearCsrfToken()
    }
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response
    }
    const envelope = response.data as ApiEnvelope<unknown>
    if (envelope.success === false) {
      const err = new Error(envelope.message || '请求失败') as Error & {
        code: string
        errors?: Record<string, string[]>
      }
      err.code = envelope.code
      return Promise.reject(err)
    }
    // 将 response.data 替换为实际 data，简化下游调用
    response.data = envelope.data as never
    return response
  },
  (error: AxiosError<ApiEnvelope<unknown>>) => {
    const envelope = error.response?.data
    const status = error.response?.status

    if (envelope?.code === 'CSRF_ERROR') {
      clearCsrfToken()
    }

    if (status === 401) {
      // 未授权，清除登录态（开发环境忽略）
      if (!ENABLE_MOCK) {
        useAuthStore.getState().clearAuth()
      }

      return Promise.reject(
        buildApiError(
          envelope?.message || '401 未授权：请检查 VITE_API_TOKEN，或确认 dev_header 模式已正确注入 X-Actor / X-Roles。',
          {
            code: envelope?.code || 'UNAUTHORIZED',
            status,
            kind: 'unauthorized',
            errors: envelope?.errors,
          },
        ),
      )
    }

    if (status === 403) {
      return Promise.reject(
        buildApiError(
          envelope?.message || '403 无权限：当前身份没有访问该接口的权限，请检查角色配置。',
          {
            code: envelope?.code || 'FORBIDDEN',
            status,
            kind: 'forbidden',
            errors: envelope?.errors,
          },
        ),
      )
    }

    if (!status) {
      // 区分 CORS / 网络不可达 / 超时等不同场景
      const errCode = (error as AxiosError & { code?: string }).code
      const errMessage = error.message || ''

      if (errCode === 'ECONNABORTED') {
        return Promise.reject(
          buildApiError(
            '请求超时：后端服务响应过慢或不可达，请检查后端服务状态与网络连接。',
            {
              code: 'TIMEOUT',
              kind: 'network',
            },
          ),
        )
      }

      // ERR_NETWORK / "Network Error" 在浏览器中通常表示 CORS 拦截或服务未启动
      if (
        errCode === 'ERR_NETWORK' ||
        errMessage.includes('Network Error')
      ) {
        return Promise.reject(
          buildApiError(
            '跨域请求被拦截，请检查 VITE_API_BASE_URL 配置或 API CORS 设置。' +
            '若使用 dev server，确认 vite.config.ts proxy 已生效且后端已启动（http://localhost:8000）。' +
            '若分域部署，确认后端 CORS_ALLOW_ORIGINS 已放行当前前端地址。' +
            '修改 .env 后请重启 Vite dev server。',
            {
              code: 'CORS_OR_NETWORK_ERROR',
              kind: 'cors',
            },
          ),
        )
      }

      return Promise.reject(
        buildApiError(
          '网络请求失败：请检查 VITE_API_BASE_URL、后端服务状态与网络连接。',
          {
            code: 'NETWORK_ERROR',
            kind: 'network',
          },
        ),
      )
    }

    return Promise.reject(
      buildApiError(
        envelope?.message || `请求失败 (${status})`,
        {
          code: envelope?.code || 'HTTP_ERROR',
          status,
          kind: 'server',
          errors: envelope?.errors,
        },
      ),
    )
  }
)

export default client
export { ENABLE_MOCK }
